import { apiFetch, type ApiTarget } from "./account-api.js";
import { CLIENT_HEADERS } from "./generated/compatibility.js";
import { withDeadline } from "./deadline.js";
import { openNoteStore } from "./note-store.js";
import { databaseName, partitionKey } from "./partition-store.js";
import { raiseLedgerRequest, type RaiseLedgerRequest } from "./platform-client.js";

export const MAX_ATTACHMENT_BYTES = 40 * 1024 * 1024;
const CHUNK_BYTES = 8 * 1024 * 1024;
export interface Attachment { id: string; name: string; path: string; bytes: ArrayBuffer; sha256: string; declaredType: string }
export interface PendingRequest { id: "pending"; revision?: string; input: RaiseLedgerRequest; attachments: Attachment[]; submitted: boolean; frozenInput?: RaiseLedgerRequest; quarantined?: boolean }
interface Published { path: string; etag: string; size: number; sha256: string; quarantined: boolean }
interface UploadStatus { state: string; error?: string; receivedParts: { part: number; size: number; sha256: string }[]; result?: Published }
export async function digest(bytes: ArrayBuffer): Promise<string> {
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
export async function draftStore(account: string, action: "read" | "write" | "remove", draft?: PendingRequest): Promise<PendingRequest | undefined> {
  if (!account.startsWith("hosted:")) throw new Error("Sign in before saving a request with attachments.");
  const store = await openNoteStore(databaseName(await partitionKey(account)), indexedDB);
  try {
    if (action !== "read") {
      if (draft === undefined) throw new Error("A saved request revision is required");
      const expected = draft.revision ?? null;
      if (action === "remove") await store.replaceRequest(expected);
      else {
        const next = { ...draft, revision: crypto.randomUUID() };
        await store.replaceRequest(expected, next);
        draft.revision = next.revision;
        return next;
      }
    }
    const saved = (await store.all<PendingRequest>("requests"))[0];
    return saved === undefined ? undefined : { ...saved, revision: saved.revision ?? "legacy" };
  } finally { store.close(); }
}
export async function selectedAttachments(files: readonly File[]): Promise<Attachment[]> {
  if (files.length > 1) throw new Error("Choose one attachment per request.");
  if (files.some((file) => file.size === 0) || files.reduce((sum, file) => sum + file.size, 0) > MAX_ATTACHMENT_BYTES) {
    throw new Error("Choose nonempty files totaling at most 40 MiB. Nothing has been submitted.");
  }
  return Promise.all(files.map(async (file) => {
    const id = crypto.randomUUID();
    const bytes = await file.arrayBuffer();
    const name = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-160) || "attachment";
    return { id, name: file.name, path: `attachments/${id}/${name}`, bytes, sha256: await digest(bytes), declaredType: file.type || "application/octet-stream" };
  }));
}
async function uploadHttp<T>(origin: ApiTarget, path: string, method = "GET", body?: BodyInit, extra: Record<string, string> = {}): Promise<T> {
  return withDeadline(async (signal) => {
    const response = await apiFetch(origin, path, { method, body, credentials: "include", signal, headers: { ...CLIENT_HEADERS, ...extra } });
    if (!response.ok) throw new Error(response.status === 401 || response.status === 403
      ? "Sign in again to resume this attachment."
      : `Attachment transfer returned HTTP ${response.status}. Your saved request is still pending; retry to resume.`);
    return await response.json() as T;
  }, 60_000, "Attachment transfer took too long. Your saved request is still pending; retry to resume.");
}
export function attachmentRequestInput(draft: PendingRequest): RaiseLedgerRequest {
  const files = draft.attachments;
  const body = draft.input.body + (files.length ? `\n\nOriginal subject: ${draft.input.subjectRef}\n\nAttachments:\n` + files.map((file) => `${file.path} (SHA-256 ${file.sha256}, ${file.bytes.byteLength} bytes)`).join("\n") : "");
  if (body.length > 8000) throw new Error("Shorten the request: its text and attachment references must total at most 8,000 characters. Nothing new has been submitted.");
  return { ...draft.input, body, subjectRef: files[0]?.path ?? draft.input.subjectRef };
}
export async function deliverRequest(origin: ApiTarget, draft: PendingRequest, progress: (text: string) => void, persist: () => Promise<void>, active: () => boolean = () => true): Promise<string> {
  const check = () => { if (!active()) throw new Error("The active account changed. Reopen the saved request to resume."); };
  const http = async <T>(...args: Parameters<typeof uploadHttp>) => { check(); const value = await uploadHttp<T>(...args); check(); return value; };
  const input = draft.frozenInput ?? attachmentRequestInput(draft);
  for (const file of draft.attachments) {
    const base = `/v1/artifacts/uploads/${file.id}`;
    progress(`Preparing ${file.name}…`);
    await http(origin, "/v1/artifacts/uploads", "POST", JSON.stringify({ intentId: file.id, path: file.path,
      size: file.bytes.byteLength, sha256: file.sha256, expectedEtag: null, declaredType: file.declaredType }), { "content-type": "application/json" });
    const status = await http<UploadStatus>(origin, base);
    if (status.state === "aborted" || status.state === "expired") throw new Error("This upload expired or was cancelled. The saved request and files remain here; contact support before retrying.");
    if (status.state === "failed") throw new Error(`The server could not publish this upload${status.error ? ` (${status.error})` : ""}. The saved request and files remain here; contact support before retrying.`);
    // A provisional result may precede audit/feed reconciliation. Only completed is acknowledged.
    let result = status.state === "completed" ? status.result : undefined;
    if (!result) {
      for (let offset = 0, part = 1; offset < file.bytes.byteLength; offset += CHUNK_BYTES, part++) {
        const bytes = file.bytes.slice(offset, offset + CHUNK_BYTES);
        const sha256 = await digest(bytes);
        const received = status.receivedParts.find((value) => value.part === part);
        if (received && (received.sha256 !== sha256 || received.size !== bytes.byteLength)) throw new Error("Stored upload bytes conflict with this saved attachment. No request was sent.");
        progress(`Uploading ${file.name}: ${Math.round(offset / file.bytes.byteLength * 100)}%`);
        if (!received) await http(origin, `${base}/parts/${part}`, "PUT", bytes, { "X-Content-SHA256": sha256 });
      }
      progress(`Verifying ${file.name}…`);
      result = await http<Published>(origin, `${base}/finalize`, "POST");
    }
    if (result.sha256 !== file.sha256 || result.size !== file.bytes.byteLength || result.path !== file.path) throw new Error("The attachment receipt did not match the saved file. No request was sent.");
    if (result.quarantined) { draft.quarantined = true; progress(`${file.name} is stored for download only; inline rendering is disabled.`); }

  }
  progress("Submitting your saved request…");
  draft.frozenInput ??= input;
  check();
  await persist();
  check();
  const outcome = await withDeadline(() => raiseLedgerRequest(origin, draft.frozenInput!), 30_000, "Request confirmation took too long. Retry checks the same saved request.");
  if (!outcome.ok) throw new Error(outcome.detail);
  return outcome.value.request.requestId;
}
